import { Component } from '@theme/component';
import { RecentlyViewed } from '@theme/recently-viewed-products';
import { sectionRenderer } from '@theme/section-renderer';

class StRecentlyViewed extends Component {
  /**
   * Product ids to show, most recent first
   * @type {string[]}
   */
  #ids = [];

  /**
   * Defers the fetch until the section is near the viewport
   * @type {IntersectionObserver}
   */
  #intersectionObserver = new IntersectionObserver(
    (entries, observer) => {
      if (!entries[0]?.isIntersecting) return;

      observer.disconnect();
      this.#load();
    },
    { rootMargin: '0px 0px 400px 0px' }
  );

  connectedCallback() {
    super.connectedCallback();

    const { productId, excludeCurrent } = this.dataset;
    this.#ids = RecentlyViewed.getProducts().filter(
      (/** @type {string} */ id) => excludeCurrent !== 'true' || id !== productId
    );

    // Nothing viewed yet: stay hidden, render nothing
    if (this.#ids.length === 0) return;

    this.classList.remove('hidden');
    this.#intersectionObserver.observe(this);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#intersectionObserver.disconnect();
  }

  /**
   * Fetches the section from the search route with an `id:` OR-query and morphs the result in.
   * The Liquid side re-orders `search.results` by `search.terms`, so recency order is preserved.
   */
  async #load() {
    const { sectionId } = this.dataset;
    if (!sectionId) throw new Error('A section ID is required');

    const url = new URL(Theme.routes.search_url, location.origin);
    url.searchParams.set('q', this.#ids.map((id) => `id:${id}`).join(' OR '));
    url.searchParams.set('resources[type]', 'product');

    try {
      await sectionRenderer.renderSection(sectionId, { url, mode: 'hydration', injectStylesheet: true });
    } catch (error) {
      // The Theme Editor can preview a section whose id the Section Rendering API doesn't know yet
      if (!Shopify.designMode) console.error('Recently viewed error:', error);
    }

    // Deleted/unpublished products drop out of search results; hide when nothing is left
    this.classList.toggle('hidden', !this.querySelector('.resource-list__item'));
  }
}

if (!customElements.get('st-recently-viewed')) {
  customElements.define('st-recently-viewed', StRecentlyViewed);
}
